/**
 * The fourteen chart kinds, as primitives.
 *
 * Every kind follows the same finish, taken from a validated data-visualisation method rather than
 * from taste: thin marks; bars rounded at their far end and square where they meet the baseline; a
 * gap of the card's own colour between touching fills; 2-unit lines; dots ringed in the card's
 * colour; hairline grids; a legend whenever there are two series or more; numbers written on the
 * chart only where they help — every bar when there are few, the last and the highest when there
 * are many; and text always in ink, never in a series colour.
 *
 * Each kind takes `t`, how far into its entrance it is. Columns rise one after another, a line
 * draws itself in, a pie sweeps round, a gauge fills, a headline number counts up. At `t = 1` the
 * drawing is the final one, which is the only one an export ever asks for.
 */
import { CHART_LIMITS, CHART_SERIES, CONTENT_WIDTHS, chartMax, chartSeries, } from '@orbit-mind/domain';
import { AXIS, FAINT, FONT, GRID, LEGEND_HEIGHT, MUTED, SMALL_FONT, bottomLabels, clamp, countUp, curveUpTo, dot, easeOutCubic, fit, formatShare, formatValue, horizontalGrid, legendRow, lerp, niceScale, portuguese, rectMark, seriesName, staged, textOf, tickText, tickWidth, widthOf, } from './chart-kit';
import { CHART_PALETTE } from './paint';
import { arc, baseline, line, move, polar, roundedBar, } from './primitives';
/** The series a kind draws: a pie draws one, a stack up to six. The rest wait in the document. */
export function drawnSeries(content) {
    return chartSeries(content).slice(0, CHART_SERIES[content.chart]);
}
/** Kinds that name their series in a legend, when they have two or more to tell apart. */
const LEGEND_KINDS = new Set([
    'line',
    'stacked',
    'compare',
    'radar',
]);
function hasLegend(content) {
    if (!LEGEND_KINDS.has(content.chart))
        return false;
    if (content.chart === 'radar' && content.points.length < 3)
        return false;
    return drawnSeries(content).length >= 2;
}
/** The room a chart needs: a width from its size, a height from its kind and its rows. */
export function chartSize(content) {
    const width = CONTENT_WIDTHS[content.size];
    const rows = content.points.length;
    const row = 14 + width / 50;
    const legend = hasLegend(content) ? LEGEND_HEIGHT + 4 : 0;
    const sized = (height) => ({
        width,
        height: Math.round(height),
    });
    switch (content.chart) {
        case 'column':
        case 'line':
        case 'area':
        case 'stacked':
            return sized(width * 0.62 + legend);
        case 'bar':
            return sized(Math.max(width * 0.3, rows * row + 2));
        case 'pie':
        case 'donut':
            return width < NARROW_CARD
                ? sized(width * 0.6 + 8 + Math.min(rows, CHART_LIMITS.slices) * LEGEND_ROW)
                : sized(width * 0.56);
        case 'compare':
            return drawnSeries(content).length < 2
                ? sized(Math.max(width * 0.3, rows * row + 2))
                : sized(legend + rows * (row + 3) + 2);
        case 'radar':
            return rows < 3 ? sized(width * 0.62) : sized(width * 0.8 + legend);
        case 'gauge':
            return sized(width * 0.6);
        case 'progress':
            if (rows > 4)
                return sized(rows * row + 2);
            return width < NARROW_CARD ? sized(width * 0.6 + 8 + rows * LEGEND_ROW) : sized(width * 0.52);
        case 'funnel':
            return sized(rows * (row + 7));
        case 'kpi':
            return sized(width * 0.5);
        case 'gantt':
            return sized(16 + rows * row);
    }
}
/**
 * A chart as primitives, and the marks a tap can find.
 *
 * `progress` is the entrance, 0 to 1; leave it out for the finished chart.
 */
export function drawChart(content, rect, context, progress = 1) {
    const frame = {
        content,
        rect,
        context,
        t: clamp(progress, 0, 1),
        primitives: [],
        marks: [],
    };
    switch (content.chart) {
        case 'column':
            columns(frame);
            break;
        case 'bar':
            bars(frame);
            break;
        case 'line':
            lines(frame, false);
            break;
        case 'area':
            lines(frame, true);
            break;
        case 'pie':
            pie(frame, false);
            break;
        case 'donut':
            pie(frame, true);
            break;
        case 'stacked':
            stacked(frame);
            break;
        case 'compare':
            compare(frame);
            break;
        case 'radar':
            radar(frame);
            break;
        case 'gauge':
            gauge(frame);
            break;
        case 'progress':
            progress_(frame);
            break;
        case 'funnel':
            funnel(frame);
            break;
        case 'kpi':
            kpi(frame);
            break;
        case 'gantt':
            gantt(frame);
            break;
    }
    return { primitives: frame.primitives, marks: frame.marks };
}
/**
 * The chart part-way from one set of numbers to another, for the moment an edit lands: bars grow
 * or shrink to their new height rather than jumping. Only between charts of the same shape — same
 * kind, same number of points and series; anything else enters afresh.
 */
export function interpolateChart(from, to, t) {
    if (!canInterpolate(from, to))
        return to;
    const e = easeOutCubic(t);
    if (e >= 1)
        return to;
    const fromSeries = chartSeries(from);
    return {
        ...to,
        points: to.points.map((point, index) => ({
            label: point.label,
            value: lerp(from.points[index]?.value ?? point.value, point.value, e),
        })),
        ...(to.series === undefined
            ? {}
            : {
                series: to.series.map((entry, k) => ({
                    name: entry.name,
                    values: entry.values.map((value, index) => lerp(fromSeries[k]?.values[index] ?? value, value, e)),
                })),
            }),
        ...(to.max === undefined || from.max === undefined ? {} : { max: lerp(from.max, to.max, e) }),
    };
}
export function canInterpolate(from, to) {
    return (from.chart === to.chart &&
        from.points.length === to.points.length &&
        chartSeries(from).length === chartSeries(to).length &&
        from.size === to.size);
}
/** A value that says whether two charts would draw the same picture. */
export function chartKey(content) {
    return JSON.stringify([
        content.chart,
        content.size,
        content.points,
        content.series ?? null,
        content.unit ?? '',
        content.max ?? null,
    ]);
}
// ————————————————————————————————————————————————————————————————— shared pieces
/** A bar's body: its colour, a shade deeper at the far end, where the value is. */
function barWash(series, end) {
    switch (end) {
        case 'top':
            return { wash: { series }, from: 1, to: 0.78, direction: 'down' };
        case 'bottom':
            return { wash: { series }, from: 0.78, to: 1, direction: 'down' };
        case 'right':
            return { wash: { series }, from: 0.78, to: 1, direction: 'right' };
        case 'left':
            return { wash: { series }, from: 1, to: 0.78, direction: 'right' };
    }
}
function horizontalLine(frame, plot, y) {
    frame.primitives.push({
        kind: 'path',
        segments: [move(plot.x, y), line(plot.x + plot.width, y)],
        stroke: 'ink',
        strokeWidth: 1,
        opacity: AXIS,
    });
}
/** The value with its unit if that fits the room, without it if that does, else nothing. */
function fitValue(frame, value, room) {
    const full = formatValue(frame, value);
    if (widthOf(frame, full, SMALL_FONT, 600) <= room)
        return full;
    const bare = formatValue(frame, value, false);
    return widthOf(frame, bare, SMALL_FONT, 600) <= room ? bare : '';
}
function single(frame, value) {
    return [{ color: 0, name: '', value: formatValue(frame, value) }];
}
function widest(frame, texts, size = FONT, weight = 400) {
    return Math.max(0, ...texts.map((text) => widthOf(frame, text, size, weight)));
}
// ————————————————————————————————————————————————————————————————— columns, bars
function columns(frame) {
    const { content, rect, t } = frame;
    const points = content.points;
    const n = points.length;
    const values = points.map((point) => point.value);
    const scale = niceScale(Math.min(0, ...values), Math.max(0, ...values), 3);
    // Either every column says its value or an axis does — never both, never neither.
    const labelled = n <= 6;
    const axis = labelled ? 0 : tickWidth(frame, scale) + 5;
    const top = rect.y + (labelled ? 12 : 5);
    const plot = {
        x: rect.x + axis,
        y: top,
        width: rect.width - axis,
        height: rect.y + rect.height - 13 - top,
    };
    const yOf = (value) => plot.y + plot.height - ((value - scale.min) / (scale.max - scale.min)) * plot.height;
    horizontalGrid(frame, plot, scale, yOf, !labelled);
    const zero = yOf(0);
    const band = plot.width / n;
    const thickness = clamp(band * 0.62, 3, 24);
    const best = values.indexOf(Math.max(...values));
    points.forEach((point, index) => {
        const grow = staged(t, index, n);
        const centre = plot.x + band * index + band / 2;
        const end = yOf(point.value * grow);
        const up = point.value >= 0;
        const height = Math.abs(zero - end);
        if (height >= 0.2) {
            frame.primitives.push({
                kind: 'path',
                segments: roundedBar({ x: centre - thickness / 2, y: Math.min(zero, end), width: thickness, height }, 4, up ? 'top' : 'bottom'),
                fill: barWash(0, up ? 'top' : 'bottom'),
            });
        }
        if (labelled || index === best || index === n - 1) {
            textOf(frame, centre, up ? end - 3 : end + SMALL_FONT + 2, fitValue(frame, point.value, band + (labelled ? -2 : 16)), { size: SMALL_FONT, weight: 600, anchor: 'middle', opacity: grow * 0.9 });
        }
        rectMark(frame, { x: plot.x + band * index, y: plot.y, width: band, height: plot.height }, { x: centre, y: yOf(point.value) }, point.label, single(frame, point.value));
    });
    horizontalLine(frame, plot, zero);
    bottomLabels(frame, points.map((point) => point.label), (index) => plot.x + band * index + band / 2, band, baseline(rect.y + rect.height - 6, SMALL_FONT));
}
function bars(frame) {
    const { content, rect, t } = frame;
    const points = content.points;
    const n = points.length;
    const row = rect.height / n;
    const thickness = clamp(row * 0.6, 3, 14);
    const values = points.map((point) => point.value);
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    const span = max - min === 0 ? 1 : max - min;
    const texts = values.map((value) => formatValue(frame, value));
    const labelWidth = Math.min(rect.width * 0.36, widest(frame, points.map((point) => point.label)) + 6);
    const valueWidth = Math.min(rect.width * 0.3, widest(frame, texts, SMALL_FONT, 600) + 5);
    const plotX = rect.x + labelWidth;
    const plotWidth = Math.max(4, rect.width - labelWidth - valueWidth);
    const xOf = (value) => plotX + ((value - min) / span) * plotWidth;
    const zero = xOf(0);
    points.forEach((point, index) => {
        const grow = staged(t, index, n);
        const middle = rect.y + row * index + row / 2;
        const top = middle - thickness / 2;
        if (min >= 0) {
            frame.primitives.push({
                kind: 'path',
                segments: roundedBar({ x: plotX, y: top, width: plotWidth, height: thickness }, 4, 'right'),
                fill: 'ink',
                opacity: 0.05,
            });
        }
        const end = xOf(point.value * grow);
        const right = point.value >= 0;
        const width = Math.abs(end - zero);
        if (width >= 0.2) {
            frame.primitives.push({
                kind: 'path',
                segments: roundedBar({ x: Math.min(zero, end), y: top, width, height: thickness }, 4, right ? 'right' : 'left'),
                fill: barWash(0, right ? 'right' : 'left'),
            });
        }
        textOf(frame, rect.x, baseline(middle, FONT), fit(frame, point.label, labelWidth - 6), {
            opacity: MUTED,
        });
        textOf(frame, (right ? Math.max(zero, end) : zero) + 4, baseline(middle, SMALL_FONT), fitValue(frame, point.value, valueWidth + (right ? 0 : plotWidth / 2)), { size: SMALL_FONT, weight: 600, opacity: grow * 0.9 });
        rectMark(frame, { x: rect.x, y: middle - row / 2, width: rect.width, height: row }, { x: xOf(point.value), y: top }, point.label, single(frame, point.value));
    });
    if (min < 0) {
        frame.primitives.push({
            kind: 'path',
            segments: [move(zero, rect.y), line(zero, rect.y + rect.height)],
            stroke: 'ink',
            strokeWidth: 1,
            opacity: AXIS,
        });
    }
}
// ————————————————————————————————————————————————————————————————— lines, areas
function lines(frame, filled) {
    const { content, rect, t } = frame;
    const series = drawnSeries(content);
    const points = content.points;
    const n = points.length;
    const multi = series.length >= 2;
    const legend = multi ? LEGEND_HEIGHT + 4 : 0;
    if (multi) {
        legendRow(frame, series.map((entry, index) => ({ name: seriesName(frame, entry.name, index), color: index })), rect.x, rect.y, rect.width);
    }
    const all = series.flatMap((entry) => entry.values);
    const scale = niceScale(Math.min(0, ...all), Math.max(0, ...all), 3);
    const axis = tickWidth(frame, scale) + 5;
    const top = rect.y + legend + 10;
    const plot = {
        x: rect.x + axis,
        y: top,
        width: rect.width - axis - 2,
        height: rect.y + rect.height - 13 - top,
    };
    const inset = 4;
    const band = n <= 1 ? plot.width : (plot.width - inset * 2) / (n - 1);
    const xOf = (index) => n <= 1 ? plot.x + plot.width / 2 : plot.x + inset + index * band;
    const yOf = (value) => plot.y + plot.height - ((value - scale.min) / (scale.max - scale.min)) * plot.height;
    horizontalGrid(frame, plot, scale, yOf, true);
    const zero = yOf(0);
    horizontalLine(frame, plot, zero);
    const reveal = easeOutCubic(t);
    const dots = n <= 8 && series.length <= 2;
    const tips = [];
    // Back to front, so the first series — the one the chart is about — is drawn on top.
    for (let s = series.length - 1; s >= 0; s -= 1) {
        const values = series[s]?.values ?? [];
        const coordinates = values.map((value, index) => ({ x: xOf(index), y: yOf(value) }));
        const first = coordinates[0];
        const { segments, tip } = curveUpTo(coordinates, reveal);
        if (first !== undefined && segments.length > 1 && (filled || !multi)) {
            frame.primitives.push({
                kind: 'path',
                segments: [...segments, line(tip.x, zero), line(first.x, zero), line(first.x, first.y)],
                fill: { wash: { series: s }, from: filled ? 0.3 : 0.13, to: 0.02, direction: 'down' },
            });
        }
        if (segments.length > 1) {
            frame.primitives.push({ kind: 'path', segments, stroke: { series: s }, strokeWidth: 2 });
        }
        if (dots) {
            coordinates.forEach((point, index) => {
                if (index === n - 1 || point.x > tip.x + 0.5)
                    return;
                dot(frame, point.x, point.y, 2.6, s, 1.2);
            });
        }
        dot(frame, tip.x, tip.y, 3.6, s, 2);
        tips.push({ x: tip.x, y: tip.y, value: values[n - 1] ?? 0, series: s });
    }
    // The last value of each line, where it ends; with one line, its highest too.
    const settle = reveal ** 3;
    if (series.length <= 2) {
        const ordered = [...tips].sort((a, b) => a.y - b.y);
        ordered.forEach((tip, order) => {
            const below = order > 0 && Math.abs(tip.y - (ordered[order - 1]?.y ?? 0)) < 12;
            textOf(frame, tip.x + 3, below ? tip.y + SMALL_FONT + 5 : tip.y - 6, formatValue(frame, tip.value), { size: SMALL_FONT, weight: 600, anchor: 'end', opacity: settle * 0.9 });
        });
    }
    if (!multi && n >= 3) {
        const values = series[0]?.values ?? [];
        const best = values.indexOf(Math.max(...values));
        if (best >= 0 && best < n - 1) {
            textOf(frame, xOf(best), yOf(values[best] ?? 0) - 6, formatValue(frame, values[best] ?? 0), {
                size: SMALL_FONT,
                weight: 600,
                anchor: 'middle',
                opacity: settle * MUTED,
            });
        }
    }
    points.forEach((point, index) => {
        rectMark(frame, { x: xOf(index) - band / 2, y: plot.y - 6, width: band, height: plot.height + 6 }, {
            x: xOf(index),
            y: Math.min(...series.map((entry) => yOf(entry.values[index] ?? 0))),
        }, point.label, series.map((entry, s) => ({
            color: s,
            name: multi ? seriesName(frame, entry.name, s) : '',
            value: formatValue(frame, entry.values[index] ?? 0),
        })));
    });
    bottomLabels(frame, points.map((point) => point.label), xOf, band, baseline(rect.y + rect.height - 6, SMALL_FONT));
}
/**
 * The slices a pie shows: all of them up to six; past that, the five largest in their own order
 * and one grey "Outros" for the rest. Seven thin wedges nobody can compare are not information.
 */
function slicesOf(frame) {
    const points = frame.content.points.map((point, index) => ({
        label: point.label,
        value: Math.max(0, point.value),
        index,
    }));
    if (points.length <= CHART_LIMITS.slices) {
        return points.map((point, index) => ({ label: point.label, value: point.value, color: index }));
    }
    const kept = new Set([...points]
        .sort((a, b) => b.value - a.value)
        .slice(0, CHART_LIMITS.slices - 1)
        .map((point) => point.index));
    const shown = points
        .filter((point) => kept.has(point.index))
        .map((point, index) => ({ label: point.label, value: point.value, color: index }));
    shown.push({
        label: portuguese(frame.context) ? 'Outros' : 'Other',
        value: points
            .filter((point) => !kept.has(point.index))
            .reduce((sum, point) => sum + point.value, 0),
        color: -1,
    });
    return shown;
}
function sector(cx, cy, inner, outer, from, to, color) {
    if (to - from >= Math.PI * 2 - 1e-6) {
        return inner <= 0
            ? { kind: 'circle', cx, cy, r: outer, fill: { series: color } }
            : {
                kind: 'circle',
                cx,
                cy,
                r: (outer + inner) / 2,
                stroke: { series: color },
                strokeWidth: outer - inner,
            };
    }
    const start = polar(cx, cy, outer, from);
    const innerEnd = polar(cx, cy, inner, to);
    const segments = inner > 0
        ? [
            move(start.x, start.y),
            ...arc(cx, cy, outer, from, to),
            line(innerEnd.x, innerEnd.y),
            ...arc(cx, cy, inner, to, from),
            line(start.x, start.y),
        ]
        : [move(cx, cy), line(start.x, start.y), ...arc(cx, cy, outer, from, to), line(cx, cy)];
    // The card's colour along every edge: two touching wedges are told apart by a gap, not a hue.
    return { kind: 'path', segments, fill: { series: color }, stroke: 'surface', strokeWidth: 1.5 };
}
function pie(frame, donut) {
    const { content, rect, t } = frame;
    const slices = slicesOf(frame);
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    const layout = roundLayout(rect, slices.length);
    const { cx, cy, r } = layout;
    const inner = donut ? r * 0.6 : 0;
    const reveal = easeOutCubic(t);
    const shareOf = (slice) => formatShare(frame, total > 0 ? slice.value / total : 0);
    const lineOf = (slice) => {
        const share = shareOf(slice);
        const value = formatValue(frame, slice.value);
        // A pie of percentages that add up to 100 would say "25%  25%": once is enough.
        return [{ color: slice.color, name: share === value ? '' : share, value }];
    };
    if (total <= 0) {
        frame.primitives.push({
            kind: 'circle',
            cx,
            cy,
            r: donut ? (r + inner) / 2 : r,
            stroke: 'ink',
            strokeWidth: donut ? r - inner : 1,
            opacity: donut ? 0.08 : 0.25,
        });
    }
    else {
        let angle = -Math.PI / 2;
        let settled = -Math.PI / 2;
        for (const slice of slices) {
            const share = slice.value / total;
            const extent = share * Math.PI * 2 * reveal;
            if (extent > 1e-4) {
                frame.primitives.push(sector(cx, cy, inner, r, angle, angle + extent, slice.color));
            }
            angle += extent;
            const final = share * Math.PI * 2;
            const mid = settled + final / 2;
            if (final > 0) {
                frame.marks.push({
                    area: { kind: 'sector', cx, cy, inner, outer: r + 2, from: settled, to: settled + final },
                    anchor: polar(cx, cy, donut ? (r + inner) / 2 : r * 0.62, mid),
                    title: slice.label,
                    lines: lineOf(slice),
                });
            }
            settled += final;
        }
    }
    if (donut && total > 0) {
        const percent = content.unit === '%';
        const biggest = slices.reduce((best, slice) => (slice.value > best.value ? slice : best));
        const headline = percent
            ? formatShare(frame, (biggest.value / total) * reveal)
            : formatValue(frame, countUp(total, reveal));
        const caption = percent ? biggest.label : 'Total';
        const size = clamp(inner * 0.46, 9, 20);
        const y = cy + size * 0.1;
        textOf(frame, cx, y, fit(frame, headline, inner * 1.7, size, 700), {
            size,
            weight: 700,
            anchor: 'middle',
        });
        textOf(frame, cx, y + SMALL_FONT + 3, fit(frame, caption, inner * 1.6, SMALL_FONT), {
            size: SMALL_FONT,
            anchor: 'middle',
            opacity: MUTED,
        });
    }
    roundLegend(frame, layout, slices.map((slice) => ({
        label: slice.label,
        share: shareOf(slice),
        color: slice.color,
        round: false,
        lines: lineOf(slice),
    })));
}
/** Narrower than this, a legend beside the circle would cut every name to three letters. */
const NARROW_CARD = 170;
const LEGEND_ROW = 13;
function roundLayout(rect, rows) {
    if (rect.width < NARROW_CARD) {
        const diameter = Math.max(8, Math.min(rect.width * 0.6, rect.height - rows * LEGEND_ROW - 6));
        return {
            cx: rect.x + rect.width / 2,
            cy: rect.y + diameter / 2 + 1,
            r: diameter / 2,
            legendX: rect.x,
            legendTop: rect.y + diameter + 6,
            legendWidth: rect.width,
            rowHeight: LEGEND_ROW,
        };
    }
    const diameter = Math.max(8, Math.min(rect.height - 4, rect.width * 0.48));
    const r = diameter / 2;
    const cx = rect.x + r + 2;
    const cy = rect.y + rect.height / 2;
    const rowHeight = Math.min(15, rect.height / Math.max(1, rows));
    return {
        cx,
        cy,
        r,
        legendX: cx + r + 12,
        legendTop: cy - (rowHeight * rows) / 2,
        legendWidth: rect.x + rect.width - (cx + r + 12),
        rowHeight,
    };
}
/** The legend of a round chart: swatch, name, share — the share in bold, because it is the answer. */
function roundLegend(frame, layout, entries) {
    const { legendX, legendTop, legendWidth, rowHeight } = layout;
    const right = legendX + legendWidth;
    entries.forEach((entry, index) => {
        const middle = legendTop + rowHeight * index + rowHeight / 2;
        frame.primitives.push({
            kind: 'rect',
            rect: { x: legendX, y: middle - 4, width: 8, height: 8 },
            radius: entry.round ? 4 : 2,
            fill: { series: entry.color },
        });
        const shareWidth = widthOf(frame, entry.share, SMALL_FONT, 700);
        textOf(frame, right, baseline(middle, SMALL_FONT), entry.share, {
            size: SMALL_FONT,
            weight: 700,
            anchor: 'end',
        });
        textOf(frame, legendX + 12, baseline(middle, SMALL_FONT), fit(frame, entry.label, legendWidth - 12 - shareWidth - 4, SMALL_FONT), { size: SMALL_FONT, opacity: MUTED });
        rectMark(frame, { x: legendX - 2, y: middle - rowHeight / 2, width: legendWidth + 2, height: rowHeight }, { x: legendX + 4, y: middle - 4 }, entry.label, entry.lines);
    });
}
// ————————————————————————————————————————————————————————————————— stacked
function stacked(frame) {
    const { content, rect, t } = frame;
    const series = drawnSeries(content);
    const points = content.points;
    const n = points.length;
    const multi = series.length >= 2;
    const legend = multi ? LEGEND_HEIGHT + 4 : 0;
    if (multi) {
        legendRow(frame, series.map((entry, index) => ({ name: seriesName(frame, entry.name, index), color: index })), rect.x, rect.y, rect.width);
    }
    const totals = points.map((_, index) => series.reduce((sum, entry) => sum + Math.max(0, entry.values[index] ?? 0), 0));
    const scale = niceScale(0, Math.max(0, ...totals), 3);
    const labelled = n <= 6;
    const axis = labelled ? 0 : tickWidth(frame, scale) + 5;
    const top = rect.y + legend + (labelled ? 12 : 5);
    const plot = {
        x: rect.x + axis,
        y: top,
        width: rect.width - axis,
        height: rect.y + rect.height - 13 - top,
    };
    const yOf = (value) => plot.y + plot.height - ((value - scale.min) / (scale.max - scale.min)) * plot.height;
    horizontalGrid(frame, plot, scale, yOf, !labelled);
    const band = plot.width / n;
    const thickness = clamp(band * 0.62, 3, 24);
    const perUnit = plot.height / (scale.max - scale.min);
    const zero = yOf(0);
    points.forEach((point, index) => {
        const grow = staged(t, index, n);
        const centre = plot.x + band * index + band / 2;
        const parts = series
            .map((entry, k) => ({ k, value: Math.max(0, entry.values[index] ?? 0) }))
            .filter((part) => part.value > 0);
        const topmost = parts[parts.length - 1]?.k;
        let bottom = zero;
        parts.forEach(({ k, value }, order) => {
            const height = value * perUnit * grow;
            // Two units of card between segments, so the stack reads as parts rather than one bar.
            const gap = order > 0 ? Math.min(2, height / 2) : 0;
            const body = {
                x: centre - thickness / 2,
                y: bottom - height,
                width: thickness,
                height: height - gap,
            };
            if (body.height >= 0.2) {
                frame.primitives.push(k === topmost
                    ? { kind: 'path', segments: roundedBar(body, 4, 'top'), fill: { series: k } }
                    : { kind: 'rect', rect: body, fill: { series: k } });
            }
            bottom -= height;
        });
        if (labelled) {
            textOf(frame, centre, bottom - 3, fitValue(frame, totals[index] ?? 0, band - 2), {
                size: SMALL_FONT,
                weight: 600,
                anchor: 'middle',
                opacity: grow * 0.9,
            });
        }
        const lines = series.map((entry, k) => ({
            color: k,
            name: multi ? seriesName(frame, entry.name, k) : '',
            value: formatValue(frame, entry.values[index] ?? 0),
        }));
        if (multi)
            lines.push({ color: null, name: 'Total', value: formatValue(frame, totals[index] ?? 0) });
        rectMark(frame, { x: plot.x + band * index, y: plot.y, width: band, height: plot.height }, { x: centre, y: yOf(totals[index] ?? 0) }, point.label, lines);
    });
    horizontalLine(frame, plot, zero);
    bottomLabels(frame, points.map((point) => point.label), (index) => plot.x + band * index + band / 2, band, baseline(rect.y + rect.height - 6, SMALL_FONT));
}
// ————————————————————————————————————————————————————————————————— before and after
function compare(frame) {
    const { content, rect, t } = frame;
    const series = drawnSeries(content);
    const before = series[0];
    const after = series[1];
    if (before === undefined || after === undefined) {
        bars(frame);
        return;
    }
    const pt = portuguese(frame.context);
    const beforeName = before.name.trim() === '' ? (pt ? 'Antes' : 'Before') : before.name;
    const afterName = after.name.trim() === '' ? (pt ? 'Depois' : 'After') : after.name;
    legendRow(frame, [
        { name: beforeName, color: -1 },
        { name: afterName, color: 0 },
    ], rect.x, rect.y, rect.width);
    const points = content.points;
    const n = points.length;
    const top = rect.y + LEGEND_HEIGHT + 4;
    const row = (rect.y + rect.height - top) / n;
    const all = [...before.values, ...after.values];
    const min = Math.min(0, ...all);
    const max = Math.max(0, ...all);
    const span = max - min === 0 ? 1 : max - min;
    const deltas = points.map((_, index) => (after.values[index] ?? 0) - (before.values[index] ?? 0));
    const deltaTexts = deltas.map((delta) => `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${formatValue(frame, Math.abs(delta))}`);
    const labelWidth = Math.min(rect.width * 0.34, widest(frame, points.map((point) => point.label)) + 6);
    const deltaWidth = Math.min(rect.width * 0.3, widest(frame, deltaTexts, SMALL_FONT, 700) + 6);
    const plotX = rect.x + labelWidth + 4;
    const plotWidth = Math.max(8, rect.width - labelWidth - deltaWidth - 12);
    const xOf = (value) => plotX + ((value - min) / span) * plotWidth;
    const tracks = [];
    points.forEach((_, index) => {
        const middle = top + row * index + row / 2;
        tracks.push(move(plotX, middle), line(plotX + plotWidth, middle));
    });
    frame.primitives.push({
        kind: 'path',
        segments: tracks,
        stroke: 'ink',
        strokeWidth: 1,
        opacity: GRID,
    });
    points.forEach((point, index) => {
        const grow = staged(t, index, n);
        const middle = top + row * index + row / 2;
        const from = xOf(before.values[index] ?? 0);
        const to = xOf(after.values[index] ?? 0);
        const now = lerp(from, to, grow);
        if (Math.abs(now - from) > 0.5) {
            frame.primitives.push({
                kind: 'path',
                segments: [move(from, middle), line(now, middle)],
                stroke: { series: 0 },
                strokeWidth: 4,
                opacity: 0.35,
            });
        }
        dot(frame, from, middle, 3.2, -1, 1.5);
        dot(frame, now, middle, 4, 0, 2);
        textOf(frame, rect.x, baseline(middle, FONT), fit(frame, point.label, labelWidth - 4), {
            opacity: MUTED,
        });
        textOf(frame, rect.x + rect.width, baseline(middle, SMALL_FONT), deltaTexts[index] ?? '', {
            size: SMALL_FONT,
            weight: 700,
            anchor: 'end',
            opacity: grow,
        });
        rectMark(frame, { x: rect.x, y: middle - row / 2, width: rect.width, height: row }, { x: to, y: middle - 4 }, point.label, [
            { color: -1, name: beforeName, value: formatValue(frame, before.values[index] ?? 0) },
            { color: 0, name: afterName, value: formatValue(frame, after.values[index] ?? 0) },
            { color: null, name: pt ? 'Diferença' : 'Change', value: deltaTexts[index] ?? '' },
        ]);
    });
}
// ————————————————————————————————————————————————————————————————— radar
function radar(frame) {
    const { content, rect, t } = frame;
    const points = content.points;
    const n = points.length;
    if (n < 3) {
        columns(frame);
        return;
    }
    const series = drawnSeries(content);
    const multi = series.length >= 2;
    const legend = multi ? LEGEND_HEIGHT + 2 : 0;
    const all = series.flatMap((entry) => entry.values);
    const max = content.max ?? niceScale(0, Math.max(0, ...all), 4).max;
    const labelRoom = Math.min(widest(frame, points.map((point) => point.label), SMALL_FONT), rect.width * 0.24);
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + (rect.height - legend) / 2;
    const radius = Math.max(8, Math.min(rect.width / 2 - labelRoom - 6, (rect.height - legend) / 2 - 11));
    const angleOf = (index) => -Math.PI / 2 + (index * Math.PI * 2) / n;
    const grid = [];
    for (const level of [0.25, 0.5, 0.75, 1]) {
        points.forEach((_, index) => {
            const corner = polar(cx, cy, radius * level, angleOf(index));
            grid.push(index === 0 ? move(corner.x, corner.y) : line(corner.x, corner.y));
        });
        const first = polar(cx, cy, radius * level, angleOf(0));
        grid.push(line(first.x, first.y));
    }
    points.forEach((_, index) => {
        const end = polar(cx, cy, radius, angleOf(index));
        grid.push(move(cx, cy), line(end.x, end.y));
    });
    frame.primitives.push({
        kind: 'path',
        segments: grid,
        stroke: 'ink',
        strokeWidth: 0.6,
        opacity: GRID * 1.3,
    });
    const grow = easeOutCubic(t);
    for (let s = series.length - 1; s >= 0; s -= 1) {
        const values = series[s]?.values ?? [];
        const corners = points.map((_, index) => polar(cx, cy, radius * clamp((values[index] ?? 0) / max, 0, 1.08) * grow, angleOf(index)));
        const segments = corners.map((corner, index) => index === 0 ? move(corner.x, corner.y) : line(corner.x, corner.y));
        const first = corners[0];
        if (first !== undefined)
            segments.push(line(first.x, first.y));
        // The first profile is washed in; the others are outlines, so overlaps stay readable.
        frame.primitives.push({
            kind: 'path',
            segments,
            fill: { series: s },
            opacity: s === 0 ? 0.16 : 0.06,
        });
        frame.primitives.push({ kind: 'path', segments, stroke: { series: s }, strokeWidth: 2 });
        if (series.length <= 2) {
            for (const corner of corners)
                dot(frame, corner.x, corner.y, 2.2, s, 1);
        }
    }
    points.forEach((point, index) => {
        const angle = angleOf(index);
        const at = polar(cx, cy, radius + 6, angle);
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        textOf(frame, at.x, sin < -0.3 ? at.y - 1 : sin > 0.3 ? at.y + SMALL_FONT - 1 : baseline(at.y, SMALL_FONT), fit(frame, point.label, labelRoom + 4, SMALL_FONT), {
            size: SMALL_FONT,
            anchor: cos > 0.3 ? 'start' : cos < -0.3 ? 'end' : 'middle',
            opacity: MUTED,
        });
        frame.marks.push({
            area: {
                kind: 'sector',
                cx,
                cy,
                inner: 0,
                outer: radius + 12,
                from: angle - Math.PI / n,
                to: angle + Math.PI / n,
            },
            anchor: polar(cx, cy, radius * clamp((series[0]?.values[index] ?? 0) / max, 0, 1.08), angle),
            title: point.label,
            lines: series.map((entry, s) => ({
                color: s,
                name: multi ? seriesName(frame, entry.name, s) : '',
                value: formatValue(frame, entry.values[index] ?? 0),
            })),
        });
    });
    if (multi) {
        legendRow(frame, series.map((entry, index) => ({ name: seriesName(frame, entry.name, index), color: index })), rect.x, rect.y + rect.height - LEGEND_HEIGHT, rect.width);
    }
}
// ————————————————————————————————————————————————————————————————— gauge, progress
function gauge(frame) {
    const { content, rect, t } = frame;
    const point = content.points[0] ?? { label: '', value: 0 };
    const max = chartMax(content);
    const grow = easeOutCubic(t);
    const fraction = clamp(point.value / max, 0, 1);
    const radius = Math.max(10, Math.min((rect.width / 2 - 3) / 1.1, (rect.height - 24) / 1.22));
    const thickness = radius * 0.2;
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + thickness / 2 + 2 + radius;
    const start = polar(cx, cy, radius, Math.PI);
    // The track is the value's own hue, lighter: one meter, not two colours.
    frame.primitives.push({
        kind: 'path',
        segments: [move(start.x, start.y), ...arc(cx, cy, radius, Math.PI, Math.PI * 2)],
        stroke: { series: 0 },
        strokeWidth: thickness,
        opacity: 0.16,
    });
    if (fraction * grow > 0.002) {
        frame.primitives.push({
            kind: 'path',
            segments: [
                move(start.x, start.y),
                ...arc(cx, cy, radius, Math.PI, Math.PI + Math.PI * fraction * grow),
            ],
            stroke: { series: 0 },
            strokeWidth: thickness,
        });
    }
    const size = clamp(radius * 0.36, 11, 28);
    textOf(frame, cx, cy - 3, fit(frame, formatValue(frame, countUp(point.value, grow)), radius * 1.45, size, 700), { size, weight: 700, anchor: 'middle' });
    textOf(frame, cx, cy + 13, fit(frame, point.label, radius * 1.3), {
        anchor: 'middle',
        opacity: MUTED,
    });
    const ends = cy + thickness / 2 + 8;
    textOf(frame, cx - radius, ends, tickText(frame, 0), {
        size: SMALL_FONT,
        anchor: 'middle',
        opacity: FAINT,
    });
    textOf(frame, cx + radius, ends, tickText(frame, max), {
        size: SMALL_FONT,
        anchor: 'middle',
        opacity: FAINT,
    });
    rectMark(frame, rect, { x: cx, y: cy - radius - thickness / 2 }, point.label, meterLines(frame, point.value, max, 0));
}
function progress_(frame) {
    const { content, rect, t } = frame;
    const points = content.points;
    const n = points.length;
    const max = chartMax(content);
    const lineOf = (value, color) => meterLines(frame, value, max, color);
    if (n > 4) {
        progressBars(frame, max, lineOf);
        return;
    }
    const layout = roundLayout(rect, n);
    const { cx, cy } = layout;
    const outer = layout.r;
    const thickness = (outer * 0.72) / (n + (n - 1) * 0.35);
    points.forEach((point, index) => {
        const r = outer - thickness / 2 - index * thickness * 1.35;
        const fraction = clamp(point.value / max, 0, 1) * staged(t, index, n);
        frame.primitives.push({
            kind: 'circle',
            cx,
            cy,
            r,
            stroke: { series: index },
            strokeWidth: thickness,
            opacity: 0.16,
        });
        if (fraction >= 0.9999) {
            frame.primitives.push({
                kind: 'circle',
                cx,
                cy,
                r,
                stroke: { series: index },
                strokeWidth: thickness,
            });
        }
        else if (fraction > 0.002) {
            const begin = polar(cx, cy, r, -Math.PI / 2);
            frame.primitives.push({
                kind: 'path',
                segments: [
                    move(begin.x, begin.y),
                    ...arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * fraction),
                ],
                stroke: { series: index },
                strokeWidth: thickness,
            });
        }
        frame.marks.push({
            area: {
                kind: 'sector',
                cx,
                cy,
                inner: r - thickness / 2 - 1,
                outer: r + thickness / 2 + 1,
                from: -Math.PI / 2,
                to: Math.PI * 1.5,
            },
            anchor: { x: cx, y: cy - r },
            title: point.label,
            lines: lineOf(point.value, index),
        });
    });
    roundLegend(frame, layout, points.map((point, index) => ({
        label: point.label,
        share: formatShare(frame, point.value / max),
        color: index,
        round: true,
        lines: lineOf(point.value, index),
    })));
}
/** What a meter says under a finger: "72%" when that is the whole story, else "36 / 50" and its share. */
function meterLines(frame, value, max, color) {
    const shown = formatValue(frame, value);
    if (frame.content.unit === '%' && max === 100)
        return [{ color, name: '', value: shown }];
    return [
        {
            color,
            name: formatShare(frame, value / max),
            value: `${shown} / ${formatValue(frame, max)}`,
        },
    ];
}
/** More than four goals: rings would be too thin to read, so they line up as bars. */
function progressBars(frame, max, lineOf) {
    const { content, rect, t } = frame;
    const points = content.points;
    const n = points.length;
    const row = rect.height / n;
    const thickness = clamp(row * 0.42, 3, 8);
    const shares = points.map((point) => formatShare(frame, point.value / max));
    const labelWidth = Math.min(rect.width * 0.36, widest(frame, points.map((point) => point.label)) + 6);
    const shareWidth = widest(frame, shares, SMALL_FONT, 700) + 6;
    const plotX = rect.x + labelWidth;
    const plotWidth = Math.max(8, rect.width - labelWidth - shareWidth);
    points.forEach((point, index) => {
        const middle = rect.y + row * index + row / 2;
        const top = middle - thickness / 2;
        frame.primitives.push({
            kind: 'rect',
            rect: { x: plotX, y: top, width: plotWidth, height: thickness },
            radius: thickness / 2,
            fill: { series: 0 },
            opacity: 0.16,
        });
        const width = plotWidth * clamp(point.value / max, 0, 1) * staged(t, index, n);
        if (width > 0.3) {
            frame.primitives.push({
                kind: 'rect',
                rect: { x: plotX, y: top, width: Math.max(width, thickness), height: thickness },
                radius: thickness / 2,
                fill: { series: 0 },
            });
        }
        textOf(frame, rect.x, baseline(middle, FONT), fit(frame, point.label, labelWidth - 6), {
            opacity: MUTED,
        });
        textOf(frame, rect.x + rect.width, baseline(middle, SMALL_FONT), shares[index] ?? '', {
            size: SMALL_FONT,
            weight: 700,
            anchor: 'end',
        });
        rectMark(frame, { x: rect.x, y: middle - row / 2, width: rect.width, height: row }, { x: plotX + plotWidth * clamp(point.value / max, 0, 1), y: top }, point.label, lineOf(point.value, 0));
    });
}
// ————————————————————————————————————————————————————————————————— funnel
function funnel(frame) {
    const { content, rect, t } = frame;
    const points = content.points;
    const n = points.length;
    const pt = portuguese(frame.context);
    const row = rect.height / n;
    const barHeight = clamp(row - 14, 5, 12);
    const largest = Math.max(0, ...points.map((point) => point.value)) || 1;
    const first = points[0]?.value ?? 0;
    const cx = rect.x + rect.width / 2;
    points.forEach((point, index) => {
        const grow = staged(t, index, n);
        const top = rect.y + row * index;
        const width = Math.max(2, (Math.max(0, point.value) / largest) * rect.width * grow);
        frame.primitives.push({
            kind: 'rect',
            rect: { x: cx - width / 2, y: top + 11, width, height: barHeight },
            radius: Math.min(3, barHeight / 2),
            fill: { series: 0 },
            opacity: n <= 1 ? 1 : 1 - (0.45 * index) / (n - 1),
        });
        const share = index === 0 ? '' : formatShare(frame, first > 0 ? point.value / first : 0);
        const shareWidth = share === '' ? 0 : widthOf(frame, share, SMALL_FONT) + 4;
        const value = formatValue(frame, point.value);
        const valueWidth = widthOf(frame, value, SMALL_FONT, 700);
        const textY = baseline(top + 5.5, FONT);
        textOf(frame, rect.x + rect.width, textY, share, {
            size: SMALL_FONT,
            anchor: 'end',
            opacity: MUTED,
        });
        textOf(frame, rect.x + rect.width - shareWidth, textY, value, {
            size: SMALL_FONT,
            weight: 700,
            anchor: 'end',
        });
        textOf(frame, rect.x, textY, fit(frame, point.label, rect.width - valueWidth - shareWidth - 8), { opacity: MUTED });
        const previous = points[index - 1]?.value;
        const lines = [{ color: 0, name: '', value }];
        if (index > 0) {
            lines.push({ color: null, name: pt ? 'Do início' : 'Of first', value: share });
            if (previous !== undefined) {
                lines.push({
                    color: null,
                    name: pt ? 'Da etapa anterior' : 'Of previous',
                    value: formatShare(frame, previous > 0 ? point.value / previous : 0),
                });
            }
        }
        rectMark(frame, { x: rect.x, y: top, width: rect.width, height: row }, { x: cx, y: top + 11 }, point.label, lines);
    });
}
// ————————————————————————————————————————————————————————————————— KPI
function kpi(frame) {
    const { content, rect, t } = frame;
    const points = content.points;
    const n = points.length;
    const last = points[n - 1] ?? { label: '', value: 0 };
    const previous = n >= 2 ? points[n - 2] : undefined;
    const grow = easeOutCubic(t);
    const spark = n >= 2;
    const size = clamp(rect.height * (spark ? 0.27 : 0.36), 14, 34);
    const left = rect.x + 2;
    const headlineY = spark ? rect.y + 13 + size * 0.8 : rect.y + rect.height / 2 + size * 0.3;
    textOf(frame, left, spark ? baseline(rect.y + 5, FONT) : headlineY - size - 2, last.label, {
        opacity: MUTED,
    });
    textOf(frame, left, headlineY, fit(frame, formatValue(frame, countUp(last.value, grow)), rect.width - 4, size, 700), { size, weight: 700 });
    let deltaLine;
    if (previous !== undefined) {
        const delta = last.value - previous.value;
        const relative = previous.value === 0 ? undefined : delta / Math.abs(previous.value);
        const amount = relative === undefined
            ? formatValue(frame, Math.abs(delta))
            : formatShare(frame, Math.abs(relative)).replace('<', '');
        const text = `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${amount}`;
        const middle = headlineY + 11;
        // The direction is the arrow's shape; the colour only repeats it.
        const arrow = delta > 0
            ? [
                move(left, middle + 3),
                line(left + 3.5, middle - 3),
                line(left + 7, middle + 3),
                line(left, middle + 3),
            ]
            : delta < 0
                ? [
                    move(left, middle - 3),
                    line(left + 3.5, middle + 3),
                    line(left + 7, middle - 3),
                    line(left, middle - 3),
                ]
                : [
                    move(left, middle - 1),
                    line(left + 7, middle - 1),
                    line(left + 7, middle + 1),
                    line(left, middle + 1),
                ];
        frame.primitives.push({
            kind: 'path',
            segments: arrow,
            fill: delta > 0 ? CHART_PALETTE.good : delta < 0 ? CHART_PALETTE.bad : 'ink',
            ...(delta === 0 ? { opacity: FAINT } : {}),
        });
        const textWidthNow = widthOf(frame, text, SMALL_FONT, 700);
        textOf(frame, left + 10, baseline(middle, SMALL_FONT), text, {
            size: SMALL_FONT,
            weight: 700,
            opacity: grow,
        });
        textOf(frame, left + 14 + textWidthNow, baseline(middle, SMALL_FONT), fit(frame, `vs ${previous.label}`, rect.width - textWidthNow - 18, SMALL_FONT), { size: SMALL_FONT, opacity: MUTED * grow });
        deltaLine = { color: null, name: `vs ${previous.label}`, value: text };
    }
    if (spark) {
        const top = headlineY + 21;
        const bottom = rect.y + rect.height - 4;
        const height = Math.max(6, bottom - top);
        const values = points.map((point) => point.value);
        const low = Math.min(...values);
        const high = Math.max(...values);
        const span = high - low === 0 ? 1 : high - low;
        const step = (rect.width - 10) / (n - 1);
        const xOf = (index) => left + index * step;
        const yOf = (value) => top + height - ((value - low) / span) * height;
        const coordinates = values.map((value, index) => ({ x: xOf(index), y: yOf(value) }));
        const { segments, tip } = curveUpTo(coordinates, grow);
        const first = coordinates[0];
        if (first !== undefined && segments.length > 1) {
            frame.primitives.push({
                kind: 'path',
                segments: [
                    ...segments,
                    line(tip.x, top + height),
                    line(first.x, top + height),
                    line(first.x, first.y),
                ],
                fill: { wash: 'ink', from: 0.08, to: 0, direction: 'down' },
            });
            frame.primitives.push({
                kind: 'path',
                segments,
                stroke: 'ink',
                strokeWidth: 1.5,
                opacity: 0.4,
            });
        }
        dot(frame, tip.x, tip.y, 3, 0, 1.5);
        points.forEach((point, index) => {
            rectMark(frame, { x: xOf(index) - step / 2, y: top - 4, width: step, height: height + 8 }, { x: xOf(index), y: yOf(point.value) }, point.label, single(frame, point.value));
        });
    }
    rectMark(frame, { x: rect.x, y: rect.y, width: rect.width, height: headlineY + 16 - rect.y }, { x: left + 12, y: headlineY - size }, last.label, [...single(frame, last.value), ...(deltaLine === undefined ? [] : [deltaLine])]);
}
/**
 * Tasks from the chart's numbers: the first series is when each starts, the second when it ends.
 * A chart with only one series — a column chart turned into a Gantt — reads its numbers as
 * durations, one task after another, which is the schedule someone typing "3, 2, 4" meant.
 */
function tasksOf(content) {
    const series = chartSeries(content);
    const ends = series[1];
    if (ends !== undefined) {
        return content.points.map((point, index) => ({
            label: point.label,
            start: point.value,
            end: Math.max(point.value, ends.values[index] ?? point.value),
        }));
    }
    let cursor = 0;
    return content.points.map((point) => {
        const start = cursor;
        cursor += Math.max(0, point.value);
        return { label: point.label, start, end: cursor };
    });
}
function gantt(frame) {
    const { content, rect, t } = frame;
    const tasks = tasksOf(content);
    const n = tasks.length;
    const pt = portuguese(frame.context);
    const low = Math.min(0, ...tasks.map((task) => task.start));
    const high = Math.max(low + 1, ...tasks.map((task) => task.end));
    const header = 16;
    const labelWidth = Math.min(rect.width * 0.36, widest(frame, tasks.map((task) => task.label)) + 8);
    const plot = {
        x: rect.x + labelWidth,
        y: rect.y + header,
        width: rect.width - labelWidth - 6,
        height: rect.height - header,
    };
    // Whole steps — weeks and days are counted, not measured — and about one per 34 units.
    const scale = niceScale(low, high, clamp(Math.floor(plot.width / 34), 2, 6), false);
    const xOf = (value) => plot.x + ((value - scale.min) / (scale.max - scale.min)) * plot.width;
    const grid = [];
    // Right to left, so the last tick — the one that carries the unit — always keeps its label and
    // a neighbour that would run into it gives way.
    let limit = Number.POSITIVE_INFINITY;
    [...scale.ticks].reverse().forEach((tick, reverseIndex) => {
        const index = scale.ticks.length - 1 - reverseIndex;
        const x = xOf(tick);
        grid.push(move(x, plot.y - 2), line(x, plot.y + plot.height));
        const last = index === scale.ticks.length - 1;
        const text = formatValue(frame, tick, last);
        const width = widthOf(frame, text, SMALL_FONT);
        const anchor = last ? 'end' : index === 0 ? 'start' : 'middle';
        const left = anchor === 'end' ? x - width : anchor === 'middle' ? x - width / 2 : x;
        const right = left + width;
        if (right + 4 > limit)
            return;
        limit = left;
        textOf(frame, x, baseline(rect.y + 6, SMALL_FONT), text, {
            size: SMALL_FONT,
            anchor,
            opacity: FAINT,
        });
    });
    frame.primitives.push({
        kind: 'path',
        segments: grid,
        stroke: 'ink',
        strokeWidth: 0.6,
        opacity: GRID,
    });
    const row = plot.height / n;
    const barHeight = clamp(row * 0.56, 4, 12);
    tasks.forEach((task, index) => {
        const grow = staged(t, index, n);
        const middle = plot.y + row * index + row / 2;
        const from = xOf(task.start);
        const to = xOf(task.end);
        const width = (to - from) * grow;
        if (width > 0.3) {
            frame.primitives.push({
                kind: 'rect',
                rect: { x: from, y: middle - barHeight / 2, width: Math.max(width, 2), height: barHeight },
                radius: Math.min(3, barHeight / 2),
                fill: barWash(0, 'right'),
            });
        }
        textOf(frame, rect.x, baseline(middle, FONT), fit(frame, task.label, labelWidth - 6), {
            opacity: MUTED,
        });
        rectMark(frame, { x: rect.x, y: middle - row / 2, width: rect.width, height: row }, { x: (from + to) / 2, y: middle - barHeight / 2 }, task.label, [
            { color: null, name: pt ? 'Início' : 'Start', value: formatValue(frame, task.start) },
            { color: null, name: pt ? 'Fim' : 'End', value: formatValue(frame, task.end) },
            {
                color: 0,
                name: pt ? 'Duração' : 'Duration',
                value: formatValue(frame, task.end - task.start),
            },
        ]);
    });
}
//# sourceMappingURL=charts.js.map